import Phaser from "phaser";
import { ENEMIES, GAME_HEIGHT, GAME_WIDTH, WALK_FRAME_SUFFIXES } from "../config";
import { Enemy, getMovementProfile, walkCycleRate, type MovementProfile } from "../entities/Enemy";
import { fx } from "../systems/FxPool";
import { goTo } from "../ui/sceneTransition";
import {
  addMenuBackdrop,
  addMenuHeader,
  beginSceneLifecycle,
  menuButton,
  MENU_SAFE_MARGIN,
  type SceneLifecycle,
} from "../ui/menuUi";
import { panel, THEME } from "../ui/theme";

/**
 * Dev-only sprite parade. Every enemy in the game stands on the muster ground
 * and walks at its real cadence, driven by the same Enemy.update() the battle
 * uses — nothing here re-implements the rig, so a sprite that looks wrong on
 * this screen is wrong in the game too.
 *
 * Clicking a unit opens the inspector: the same walk, stoppable and steppable
 * frame by frame, with the texture key it is currently showing.
 */

const GROUPS: { id: MovementProfile; title: string; note: string }[] = [
  { id: "heavy",  title: "SCHWERER TRITT", note: "Bosse, Troll, Golem und Rammbock — der langsamste Takt im Spiel." },
  { id: "walk",   title: "MARSCH",         note: "Der Normalgang: deutliches Heben zwischen den Schritten, klarer Aufsatz." },
  { id: "scurry", title: "HETZEN",         note: "Leicht und schnell, kaum Bodenkontakt." },
  { id: "hover",  title: "SCHWEBEN",       note: "Kein Fuß berührt den Boden: Flügelschlag und treibende Schleier statt Schritten." },
  { id: "roll",   title: "ROLLEN",         note: "Räder statt Beine — die Speichen drehen sich, ein Beinwechsel wäre schlicht falsch." },
];

const COLS = 6;
const CARD_W = 185;
const STAGE_H = 96;
const CARD_H = STAGE_H + 24;
const GAP_X = 20;
const GAP_Y = 18;
const VIEW_TOP = 92;
const VIEW_BOTTOM = GAME_HEIGHT - 30;
const CONTENT_X = MENU_SAFE_MARGIN + 2;

/** Anything the scroll offset moves and the content mask clips. */
type Movable = Phaser.GameObjects.GameObject & {
  y: number;
  setMask(mask: Phaser.Display.Masks.GeometryMask): unknown;
};

interface Card {
  key: string;
  name: string;
  profile: MovementProfile;
  frames: string[];
  enemy: Enemy;
  image: Phaser.GameObjects.Image;
  chip: Phaser.GameObjects.Text;
}

export class SpriteShowerScene extends Phaser.Scene {
  private life!: SceneLifecycle;
  private cards: Card[] = [];
  private scrollables: { o: Movable; base: number }[] = [];
  private scroll = 0;
  private scrollMax = 0;
  private thumb?: Phaser.GameObjects.Rectangle;
  private paused = false;
  private dust = true;
  private pauseLabel?: Phaser.GameObjects.Text;
  private dustLabel?: Phaser.GameObjects.Text;

  /** Open inspector, or undefined when closed. */
  private inspector?: {
    card: Card;
    root: Phaser.GameObjects.Container;
    image: Phaser.GameObjects.Image;
    chips: Phaser.GameObjects.Text[];
    info: Phaser.GameObjects.Text;
    modeLabel: Phaser.GameObjects.Text;
    /** null = mirror the live unit; a number = hold that frame. */
    frame: number | null;
  };

  constructor() { super("SpriteShowerScene"); }

  create(): void {
    // Phaser reuses the instance across shutdown/start — reset everything.
    this.life = beginSceneLifecycle(this);
    this.cards = [];
    this.scrollables = [];
    this.scroll = 0;
    this.scrollMax = 0;
    this.thumb = undefined;
    this.paused = false;
    this.dust = true;
    this.inspector = undefined;

    addMenuBackdrop(this, { darkness: 0.9 });

    // Footstep dust comes from the real FxPool, so it fires on exactly the
    // frame the texture swaps — same as in battle.
    fx.attach(this);
    this.life.add(() => fx.detach(this));

    const mask = this.buildContentMask();
    this.buildRoll(mask);
    this.buildChrome();

    this.life.key("keydown-ESC", () => (this.inspector ? this.closeInspector() : goTo(this, "MenuScene")));
    this.life.key("keydown-SPACE", () => this.togglePaused());
    this.life.key("keydown-DOWN", () => this.setScroll(this.scroll + 60));
    this.life.key("keydown-UP", () => this.setScroll(this.scroll - 60));
    this.life.input("wheel", (...args: unknown[]) => {
      const dy = args[3] as number;
      this.setScroll(this.scroll + dy * 0.5);
    });

    this.setScroll(0);
  }

  /* ------------------------------------------------------------------ */
  /*                              Building                              */
  /* ------------------------------------------------------------------ */

  private buildContentMask(): Phaser.Display.Masks.GeometryMask {
    const g = this.add.graphics();
    g.fillStyle(0xffffff, 1);
    g.fillRect(MENU_SAFE_MARGIN, VIEW_TOP, GAME_WIDTH - MENU_SAFE_MARGIN * 2, VIEW_BOTTOM - VIEW_TOP);
    g.setVisible(false);
    this.life.add(() => g.destroy());
    return g.createGeometryMask();
  }

  private buildRoll(mask: Phaser.Display.Masks.GeometryMask): void {
    const track = <T extends Movable>(o: T, base: number): T => {
      o.setMask(mask);
      this.scrollables.push({ o, base });
      return o;
    };

    const units = Object.values(ENEMIES).map(stats => ({
      stats,
      profile: getMovementProfile(stats.key, !!stats.flying, !!stats.boss),
    }));

    let cy = VIEW_TOP + 12;
    for (const group of GROUPS) {
      const members = units.filter(u => u.profile === group.id);
      if (members.length === 0) continue;

      const count = `${members.length} ${members.length === 1 ? "Einheit" : "Einheiten"}`;
      track(this.add.text(CONTENT_X, cy, group.title, {
        fontFamily: THEME.fonts.display, fontSize: "21px", color: THEME.colors.gold,
      }), cy);
      track(this.add.text(CONTENT_X + 260, cy + 6, `${count} · ${cadenceLabel(group.id)}`, {
        fontFamily: THEME.fonts.body, fontSize: "13px", color: THEME.colors.parchmentDim,
      }), cy + 6);
      cy += 26;
      track(this.add.text(CONTENT_X, cy, group.note, {
        fontFamily: THEME.fonts.body, fontSize: "13px", color: THEME.colors.parchmentDim,
      }).setAlpha(0.75), cy);
      cy += 24;

      members.forEach((member, index) => {
        const col = index % COLS;
        const row = Math.floor(index / COLS);
        const x = CONTENT_X + col * (CARD_W + GAP_X);
        const y = cy + row * (CARD_H + GAP_Y);
        this.buildCard(member.stats.key, member.profile, x, y, track);
      });

      cy += Math.ceil(members.length / COLS) * (CARD_H + GAP_Y) + 16;
    }

    this.scrollMax = Math.max(0, cy - VIEW_TOP - (VIEW_BOTTOM - VIEW_TOP));
  }

  private buildCard(
    key: string,
    profile: MovementProfile,
    x: number,
    y: number,
    track: <T extends Movable>(o: T, base: number) => T,
  ): void {
    const stats = ENEMIES[key];
    const frames = WALK_FRAME_SUFFIXES
      .map(suffix => `enemy-${key}${suffix}`)
      .filter(k => this.textures.exists(k));

    const stage = track(this.add.rectangle(x, y, CARD_W, STAGE_H, 0x1d2418, 0.92).setOrigin(0), y);
    stage.setStrokeStyle(1, THEME.hex.ironEdge, 0.45);
    stage.setInteractive({ useHandCursor: true });
    stage.on("pointerover", () => stage.setFillStyle(0x27301f, 0.95));
    stage.on("pointerout", () => stage.setFillStyle(0x1d2418, 0.92));

    // A real Enemy, standing still: speed 0 and a waypoint far enough away that
    // it never arrives (arriving would end the unit and freeze the rig).
    const centreX = x + CARD_W / 2;
    const centreY = y + STAGE_H / 2 + 6;
    const enemy = new Enemy(
      this,
      { ...stats, speed: 0 },
      [{ x: centreX, y: centreY }, { x: centreX, y: centreY + 1e6 }],
    );
    // Health bars are battle information, not sprite information.
    for (const child of enemy.list) {
      if (child instanceof Phaser.GameObjects.Rectangle) child.setVisible(false);
    }
    const zoom = Phaser.Math.Clamp(Math.floor((STAGE_H * 0.62) / (stats.radius * 2)), 1, 3);
    enemy.setScale(zoom);
    track(enemy, centreY);

    const image = enemy.list.find(o => o instanceof Phaser.GameObjects.Image) as Phaser.GameObjects.Image;

    const label = track(this.add.text(x + 8, y + STAGE_H + 5, prettyName(key), {
      fontFamily: THEME.fonts.body, fontSize: "13px",
      color: stats.boss ? THEME.colors.gold : THEME.colors.parchment,
    }), y + STAGE_H + 5);
    label.setName(`sprite-card-${key}`);

    const chip = track(this.add.text(x + CARD_W - 8, y + STAGE_H + 5, "A", {
      fontFamily: THEME.fonts.body, fontSize: "13px",
      color: frames.length > 1 ? THEME.colors.gold : THEME.colors.lock,
    }).setOrigin(1, 0), y + STAGE_H + 5);

    const card: Card = { key, name: prettyName(key), profile, frames, enemy, image, chip };
    stage.on("pointerdown", () => this.openInspector(card));
    this.cards.push(card);
  }

  private buildChrome(): void {
    const multi = this.cards.filter(c => c.frames.length > 1).length;
    addMenuHeader(this, {
      title: "SPRITE SHOWER",
      subtitle: `${this.cards.length} Einheiten · ${multi} animiert · ${this.cards.length - multi} mit einem Bild`,
      onBack: () => goTo(this, "MenuScene"),
      rightLabel: "PAUSE",
      onRight: () => this.togglePaused(),
    });
    this.pauseLabel = this.children.getByName("menu-header-action")
      ?.getData("label") as Phaser.GameObjects.Text | undefined;

    const dustBtn = menuButton(this, GAME_WIDTH - 244, 47, 130, 38, "STAUB: AN", {
      name: "sprite-dust", fontSize: 13, onClick: () => this.toggleDust(),
    });
    this.dustLabel = dustBtn.getData("label") as Phaser.GameObjects.Text;

    // Scroll indicator: a plain track and thumb, right of the cards.
    const trackX = GAME_WIDTH - MENU_SAFE_MARGIN - 12;
    this.add.rectangle(trackX, VIEW_TOP, 4, VIEW_BOTTOM - VIEW_TOP, THEME.hex.ironDeep, 0.6).setOrigin(0);
    this.thumb = this.add.rectangle(trackX, VIEW_TOP, 4, 60, THEME.hex.goldDim, 0.9).setOrigin(0);

    this.add.text(GAME_WIDTH / 2, GAME_HEIGHT - 16,
      "Mausrad scrollt · Klick öffnet die Einzelansicht · LEER pausiert · ESC zurück", {
      fontFamily: THEME.fonts.body, fontSize: "12px", color: THEME.colors.lock,
    }).setOrigin(0.5);
  }

  /* ------------------------------------------------------------------ */
  /*                              Controls                              */
  /* ------------------------------------------------------------------ */

  private setScroll(value: number): void {
    this.scroll = Phaser.Math.Clamp(value, 0, this.scrollMax);
    for (const s of this.scrollables) s.o.y = s.base - this.scroll;

    if (!this.thumb) return;
    const viewH = VIEW_BOTTOM - VIEW_TOP;
    const ratio = viewH / (viewH + this.scrollMax);
    const thumbH = Math.max(40, viewH * ratio);
    this.thumb.height = thumbH;
    this.thumb.y = VIEW_TOP + (this.scrollMax === 0 ? 0 : (this.scroll / this.scrollMax) * (viewH - thumbH));
  }

  private togglePaused(): void {
    this.paused = !this.paused;
    this.pauseLabel?.setText(this.paused ? "WEITER" : "PAUSE");
  }

  private toggleDust(): void {
    this.dust = !this.dust;
    if (this.dust) fx.attach(this);
    else fx.detach(this);
    this.dustLabel?.setText(this.dust ? "STAUB: AN" : "STAUB: AUS");
  }

  /* ------------------------------------------------------------------ */
  /*                             Inspector                              */
  /* ------------------------------------------------------------------ */

  private openInspector(card: Card): void {
    if (this.inspector) this.closeInspector();

    const root = this.add.container(0, 0).setDepth(THEME.depths.overlay);
    const dim = this.add.rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x080502, 0.78).setOrigin(0);
    dim.setInteractive();
    dim.on("pointerdown", () => this.closeInspector());
    root.add(dim);

    const w = 660, h = 430;
    const px = (GAME_WIDTH - w) / 2, py = (GAME_HEIGHT - h) / 2;
    root.add(panel(this, px, py, w, h, "iron"));

    root.add(this.add.text(px + w / 2, py + 26, card.name, {
      fontFamily: THEME.fonts.display, fontSize: "26px", color: THEME.colors.gold,
    }).setOrigin(0.5));
    root.add(this.add.text(px + w / 2, py + 54, `${card.profile} · ${cadenceLabel(card.profile)} · ${card.frames.length} Bild(er)`, {
      fontFamily: THEME.fonts.body, fontSize: "14px", color: THEME.colors.parchmentDim,
    }).setOrigin(0.5));

    const stageY = py + 76;
    root.add(this.add.rectangle(px + 30, stageY, w - 60, 210, 0x1d2418, 0.95).setOrigin(0)
      .setStrokeStyle(1, THEME.hex.ironEdge, 0.45));
    const image = this.add.image(px + w / 2, stageY + 105, card.frames[0]);
    root.add(image);

    const chips: Phaser.GameObjects.Text[] = [];
    const chipY = stageY + 226;
    card.frames.forEach((key, i) => {
      const cx = px + w / 2 + (i - (card.frames.length - 1) / 2) * 42;
      const chip = this.add.text(cx, chipY, String.fromCharCode(65 + i), {
        fontFamily: THEME.fonts.display, fontSize: "18px", color: THEME.colors.lock,
      }).setOrigin(0.5);
      chip.setInteractive({ useHandCursor: true });
      chip.on("pointerdown", () => this.showFrame(i));
      chip.setData("texture", key);
      chips.push(chip);
      root.add(chip);
    });

    const modeLabel = this.add.text(px + w / 2, chipY + 30, "", {
      fontFamily: THEME.fonts.body, fontSize: "13px", color: THEME.colors.parchmentDim,
    }).setOrigin(0.5);
    root.add(modeLabel);

    const info = this.add.text(px + w / 2, py + h - 74, "", {
      fontFamily: THEME.fonts.body, fontSize: "13px", color: THEME.colors.parchment,
    }).setOrigin(0.5);
    root.add(info);

    const by = py + h - 38;
    root.add(menuButton(this, px + w / 2 - 190, by, 96, 38, "‹ BILD", {
      name: "inspector-prev", fontSize: 13, onClick: () => this.stepFrame(-1),
    }));
    root.add(menuButton(this, px + w / 2 - 64, by, 120, 38, "LIVE", {
      name: "inspector-live", fontSize: 13, onClick: () => this.showFrame(null),
    }));
    root.add(menuButton(this, px + w / 2 + 62, by, 96, 38, "BILD ›", {
      name: "inspector-next", fontSize: 13, onClick: () => this.stepFrame(1),
    }));
    root.add(menuButton(this, px + w / 2 + 190, by, 96, 38, "ZURÜCK", {
      name: "inspector-close", fontSize: 13, onClick: () => this.closeInspector(),
    }));

    this.inspector = { card, root, image, chips, info, modeLabel, frame: null };
    this.refreshInspector();
  }

  private closeInspector(): void {
    this.inspector?.root.destroy(true);
    this.inspector = undefined;
  }

  private showFrame(frame: number | null): void {
    if (!this.inspector) return;
    this.inspector.frame = frame;
    this.refreshInspector();
  }

  private stepFrame(direction: number): void {
    const insp = this.inspector;
    if (!insp) return;
    const count = insp.card.frames.length;
    const current = insp.frame ?? this.frameIndexOf(insp.card);
    this.showFrame(Phaser.Math.Wrap(current + direction, 0, count));
  }

  private refreshInspector(): void {
    const insp = this.inspector;
    if (!insp) return;
    const { card, image } = insp;
    const live = insp.frame === null;
    const index = live ? this.frameIndexOf(card) : insp.frame!;
    const key = card.frames[index] ?? card.frames[0];

    image.setTexture(key);
    const source = this.textures.get(key).getSourceImage();
    const zoom = Phaser.Math.Clamp(Math.floor(170 / Math.max(source.width, source.height)), 2, 8);
    if (live) {
      // Mirror the unit's own pose so the big view and the card agree.
      image.setPosition(image.x, image.y);
      image.setAngle(card.image.angle);
      image.setScale(zoom * card.image.scaleX, zoom * card.image.scaleY);
    } else {
      image.setAngle(0);
      image.setScale(zoom);
    }

    insp.chips.forEach((chip, i) => {
      chip.setColor(i === index ? THEME.colors.gold : THEME.colors.lock);
    });
    insp.modeLabel.setText(live ? "LIVE — läuft mit dem Spiel" : "ANGEHALTEN — Bild für Bild");
    insp.info.setText(`${key}   ·   ${source.width}×${source.height} px   ·   ${zoom}× vergrößert`);
  }

  private frameIndexOf(card: Card): number {
    const i = card.frames.indexOf(card.image.texture.key);
    return i < 0 ? 0 : i;
  }

  /* ------------------------------------------------------------------ */
  /*                                Tick                                */
  /* ------------------------------------------------------------------ */

  update(_time: number, delta: number): void {
    if (!this.paused) {
      for (const card of this.cards) {
        card.enemy.update(0, delta);
        // Enemy.update sets depth from its own y for battlefield sorting; here
        // the roll is a fixed grid, so pin every unit above the card stages.
        card.enemy.setDepth(THEME.depths.units);
      }
    }
    for (const card of this.cards) {
      const letter = String.fromCharCode(65 + this.frameIndexOf(card));
      if (card.chip.text !== letter) card.chip.setText(letter);
    }
    fx.update(delta);
    if (this.inspector) this.refreshInspector();
  }
}

/** Stepping gaits are quoted per footfall; floaters and rollers have no
 *  footfall, so quoting a stride time for them would be fiction. */
function cadenceLabel(profile: MovementProfile): string {
  const cycleMs = (Math.PI * 2) / walkCycleRate(profile);
  const steps = profile === "walk" || profile === "heavy" || profile === "scurry";
  return steps
    ? `${Math.round(cycleMs / 2)} ms je Schritt`
    : `${Math.round(cycleMs)} ms je Zyklus`;
}

/** "boss_doom_bringer" → "DOOM BRINGER". */
function prettyName(key: string): string {
  return key.replace(/^boss_/, "").replace(/_/g, " ").toUpperCase();
}
